import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { forkJoin, Observable, Subject } from 'rxjs';
import { first, switchMap, take, takeUntil } from 'rxjs/operators';
import { UsersService } from '../../../core/data/users.service';
import { Application } from '../../../core/models/application.model';
import { Roles } from '../../../core/models/roles.model';
import { User } from '../../../core/models/user.model';
import { ApplicationService } from '../application.service';

class UserView {
  constructor(
    public userId: number,
    public email: string,
    public role: string
  ) {}
}

@Component({
  selector: 'idl-application-users',
  templateUrl: './application-users.component.html',
  styleUrls: ['./application-users.component.scss']
})
export class ApplicationUsersComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  application: Application;
  displayData: UserView[] = [];
  developers: User[];
  developer: User;
  managers: User[];
  manager: User;

  displayedColumns: string[] = ['remove', 'email', 'role'];
  dataSource = new MatTableDataSource<UserView>();

  @ViewChild(MatSort, { static: true }) sort: MatSort;

  constructor(
    private _applicationService: ApplicationService,
    private _route: ActivatedRoute,
    private usersService: UsersService,
    private router: Router,
    private _snackBar: MatSnackBar
  ) {
    this._route.parent.paramMap
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((params: ParamMap) =>
          this._applicationService.getApp(+params.get('id'))
        )
      )
      .subscribe(app => (this.application = app));

    this.usersService
      .list(null, Roles[Roles.Developer])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(data => {
        this.developers = data;
      });

    this.usersService
      .list(null, Roles[Roles.Manager])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(data => {
        this.managers = data;
      });
  }

  /**
   * Loads application users
   */
  loadAppUsers() {
    this.displayData = [];

    const roles: string[] = [];
    const roleGets: Observable<User[]>[] = [];

    roles.push(Roles[Roles.Manager]);
    roles.push(Roles[Roles.Developer]);

    roles.forEach(role => {
      roleGets.push(
        this.usersService
          .list(this.application.applicationId, role)
          .pipe(first())
      );
    });

    forkJoin(roleGets)
      .pipe(take(1))
      .subscribe(results => {
        let i = 0;
        results.forEach(users => {
          // console.log(users);
          users.forEach(user => {
            this.displayData.push(
              new UserView(user.userId, user.email, roles[i])
            );
          });
          ++i;
        });
        this.dataSource = new MatTableDataSource(this.displayData);
        setTimeout(() => {
          this.dataSource.sort = this.sort;
        });
      });
  }

  ngOnInit(): void {
    this.loadAppUsers();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Applys table filter
   * @param filterValue
   */
  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  onRowClick(event): void {
    this.router.navigate(['/profile', event.data.userId, { enableBack: true }]);
  }

  /**
   * Adds user role to an application
   * @param userId to add role for
   * @param role to add for user Id
   * @param appId app that we are adding to
   */
  addUserRole(userId: number, role: string, appId: number) {
    this.usersService
      .addrole(userId, role, appId)
      .pipe(take(1))
      .subscribe(data => {
        this._snackBar.open('New user role created.', `Added ${role}`, {
          duration: 5000,
          verticalPosition: 'top'
        });

        this.loadAppUsers();
      });
  }

  addDeveloper() {
    if (this.developer) {
      this.addUserRole(
        this.developer.userId,
        Roles[Roles.Developer],
        this.application.applicationId
      );
    }
  }

  addManager() {
    if (this.manager) {
      this.addUserRole(
        this.manager.userId,
        Roles[Roles.Manager],
        this.application.applicationId
      );
    }
  }

  removeUser(user: UserView) {
    this.usersService
      .deleterole(user.userId, user.role, this.application.applicationId)
      .pipe(take(1))
      .subscribe(
        result => {
          this._snackBar.open(user.email, `Removed ${user.role}:`, {
            duration: 5000,
            verticalPosition: 'top'
          });

          this.loadAppUsers();
        },
        err => {
          this._snackBar.open('Error removing user.', `Error`, {
            duration: 5000,
            verticalPosition: 'top',
            panelClass: 'mat-warn'
          });
        }
      );
  }
}
